import { sha256 } from 'js-sha256';
import moment from 'moment';

import { GTM_ENABLED } from '@/config';
import { EUR, NEW_ACCOUNT_DATE_FORMAT, UNKNOWN } from '@/config/appConstants';
import { BillingDetails } from '@/features/billing';
import gtmAddLogged from '@/lib/gtm/gtmAddLogged.json';
import gtmAddNotLogged from '@/lib/gtm/gtmAddNotLogged.json';
import gtmAddToCart from '@/lib/gtm/gtmAddToCart.json';
import gtmCheckoutData from '@/lib/gtm/gtmCheckoutData.json';
import gtmCompletedBillingData from '@/lib/gtm/gtmCompletedBillingData.json';
import gtmPaidPlanData from '@/lib/gtm/gtmPaidPlanData.json';
import gtmTokenRequest from '@/lib/gtm/gtmTokenRequestData.json';
import gtmTokenValidated from '@/lib/gtm/gtmTokenValidatedData.json';
import {
  GTMAddEventTypes,
  GTMCheckoutEventTypes,
  GTMCompletedBillingEventTypes,
  GTMProductsTypes,
  GTMPaidPlanEventTypes,
  GTMUserNavigationTypes,
  GTMUserDataTypes,
} from '@/lib/gtm/gtmTypes';
import gtmValidationAccountData from '@/lib/gtm/gtmValidationAccountData.json';
import userNavigationData from '@/lib/gtm/userNavigationData.json';
import {
  Currency,
  iAuthInfo,
  iUserSetupData,
  Me,
  PricesTypes,
  ProductTypes,
  UserData,
  UserProductTypes,
} from '@/types';
import {
  filterUserProductsByActive,
  filterUserProductsByPending,
  getProductLicensedPriceByCurrency,
  isNewFreeUser,
  getOrigin,
} from '@/utils/helpers';

export const isGTMEnabled = (): boolean => eval(GTM_ENABLED as unknown as string) === true;

export const sendEventGtm = (
  gtmData: any,
  GTMDriver: (
    data:
      | GTMUserNavigationTypes
      | GTMAddEventTypes
      | GTMPaidPlanEventTypes
      | GTMCompletedBillingEventTypes
  ) => void,
  index = 0
): void => {
  if (!isGTMEnabled()) return;
  if (index == 5) return;

  if (typeof window.dataLayer === 'undefined') {
    setTimeout(() => {
      sendEventGtm(gtmData, GTMDriver, parseInt(index as unknown as string, 10) + 1);
    }, 1000);
  } else {
    GTMDriver(gtmData);
  }
};

export const buildGTMAddToCartRequest = (userData: iUserSetupData): GTMAddEventTypes => {
  const gtmData = gtmAddToCart as GTMAddEventTypes;
  gtmData.ecommerce.add.products[0].id = userData.productId;
  gtmData.ecommerce.add.products[0].name = userData.productName;
  gtmData.ecommerce.add.products[0].lang = userData.lang;

  return gtmData;
};

export const buildGTMTokenRequest = (me: Me): GTMCompletedBillingEventTypes => {
  const gtmData = gtmTokenRequest as GTMCompletedBillingEventTypes;
  gtmData.ecommerce.navigation.userData = buildGTMUserData(
    me,
    gtmData.ecommerce.navigation.userData
  );
  gtmData.ecommerce.navigation.userData.validatedAt = new Date().toLocaleString();

  return gtmData;
};

export const buildGTMTokenValidated = (me: Me): GTMCompletedBillingEventTypes => {
  const gtmData = gtmTokenValidated as GTMCompletedBillingEventTypes;
  gtmData.ecommerce.navigation.userData = buildGTMUserData(
    me,
    gtmData.ecommerce.navigation.userData
  );
  gtmData.ecommerce.navigation.userData.validatedAt = new Date().toLocaleString();

  return gtmData;
};

export const buildGTMAddLogged = (userData: UserData): GTMAddEventTypes => {
  const gtmData = gtmAddLogged as GTMAddEventTypes;
  gtmData.ecommerce.add.products[0].lang = userData.lang;
  gtmData.ecommerce.add.products[0].gruid = sha256(userData.groupId);
  gtmData.ecommerce.add.products[0].groupCreatedAt = userData.groupCreatedAt;

  return gtmData;
};

export const buildGTMNavigationData = (
  userData: UserData,
  billingDetails: BillingDetails,
  userProducts: UserProductTypes[]
): GTMUserNavigationTypes => {
  const gtmData = userNavigationData as GTMUserNavigationTypes;
  const pendingProduct = filterUserProductsByPending(userProducts);
  const activeProduct = filterUserProductsByActive(userProducts);

  // Session
  gtmData.ecommerce.navigation.sessionData.currentURL = window.location.href;
  gtmData.ecommerce.navigation.sessionData.currentTime = moment().format(NEW_ACCOUNT_DATE_FORMAT);
  gtmData.ecommerce.navigation.sessionData.currentLang = userData.lang;

  // UserData (Billing Details)
  billingDetails.id = billingDetails.groupId;
  gtmData.ecommerce.navigation.userData = Object.assign(
    { alreadyPhoneTokenValidated: !isNewFreeUser(userData) },
    billingDetails
  );

  // Pending Product
  pendingProduct && (gtmData.ecommerce.navigation.pendingProduct = pendingProduct.product);

  // Product (only one at this moment)
  activeProduct && (gtmData.ecommerce.navigation.activeProducts[0] = activeProduct.product);

  return gtmData;
};

export const buildGTMInformationData = (): GTMAddEventTypes => {
  return gtmAddLogged as GTMAddEventTypes;
};

export const buildGTMCompletedBilling = (me: Me): GTMCompletedBillingEventTypes => {
  const gtmData = gtmCompletedBillingData as GTMCompletedBillingEventTypes;
  gtmData.ecommerce.navigation.userData = buildGTMUserData(
    me,
    gtmData.ecommerce.navigation.userData
  );

  return gtmData;
};

export const buildGTMValidationAccount = (me: Me): GTMCompletedBillingEventTypes => {
  const gtmData = gtmValidationAccountData as GTMCompletedBillingEventTypes;

  gtmData.ecommerce.navigation.userData = buildGTMUserData(
    me,
    gtmData.ecommerce.navigation.userData
  );
  gtmData.ecommerce.navigation.userData.validatedAt = new Date().toLocaleString();

  return gtmData;
};

export const buildGTMAddNotLogged = (): GTMAddEventTypes => {
  return gtmAddNotLogged as GTMAddEventTypes;
};

export const buildGTMCheckoutData = (
  product: ProductTypes,
  landingname: string | undefined,
  currency: Currency | undefined,
  authInfo: iAuthInfo,
  lang: string
): GTMCheckoutEventTypes => {
  const productPrice = getProductLicensedPriceByCurrency(product.prices, currency || EUR);
  const gtmData = gtmCheckoutData as GTMCheckoutEventTypes;
  const newProductData: GTMProductsTypes = {
    name: product.category || UNKNOWN,
    id: product.mdirectorId || UNKNOWN,
    price: productPrice?.amount || UNKNOWN,
    brand: 'Mdirector',
    category: product.info.categoryName || UNKNOWN,
    variant: product.name || UNKNOWN,
    quantity: 1,
    coupon: '',
    lang: lang,
    groupCreatedAt: moment().format(NEW_ACCOUNT_DATE_FORMAT),
    gruid: sha256(authInfo.getGroupId()),
  };

  gtmData.ecommerce.checkout.actionField.option = landingname || UNKNOWN;
  gtmData.ecommerce.checkout.products = [newProductData];

  return gtmData;
};

export const buildGTMPaidPlanData = (
  product: ProductTypes,
  price: PricesTypes | undefined,
  lang: string,
  me: Me
): GTMPaidPlanEventTypes => {
  const gtmData = gtmPaidPlanData as GTMPaidPlanEventTypes;
  const newProductData: GTMProductsTypes = {
    name: product.category || UNKNOWN,
    id: product.mdirectorId || UNKNOWN,
    price: price?.amount || UNKNOWN,
    brand: 'Mdirector',
    category: product.info.categoryName || UNKNOWN,
    variant: product.name || UNKNOWN,
    quantity: 1,
    coupon: '',
    lang: lang,
    groupCreatedAt: me.groupInfo.createdAt,
    gruid: sha256(me.groupInfo.id),
    periodicity: getOrigin(product),
  };

  gtmData.ecommerce.purchase.actionField.id = price?.id || UNKNOWN;
  gtmData.ecommerce.navigation.activeProducts = [newProductData];
  gtmData.ecommerce.navigation.userData = buildGTMUserData(
    me,
    gtmData.ecommerce.navigation.userData
  );

  return gtmData;
};

const buildGTMUserData = (me: Me, userData: GTMUserDataTypes): GTMUserDataTypes => {
  userData.id = me.groupInfo.id ? me.groupInfo.id : UNKNOWN;
  userData.gruid = me.groupInfo.id ? sha256(me.groupInfo.id) : UNKNOWN;
  userData.currentLang = me.userInfo.language;
  userData.createdAt = me.groupInfo.createdAt;

  return userData;
};
